// ContactForm.js (Enhanced Professional Version)

import React, { useState, useEffect } from 'react';
import { supabase } from '../lib/supabaseClient';
import {
  ChatBubbleLeftEllipsisIcon,
  CalendarDaysIcon,
  CheckCircleIcon,
  ExclamationTriangleIcon,
} from '@heroicons/react/24/outline';

const INITIAL_CONTACT = { name: '', email: '', message: '' };
const INITIAL_ENQUIRY = {
  full_name: '',
  email: '',
  phone: '',
  event_date: '',
  location: '',
  services: {},
  Budget: '',
  details: '',
};

export default function ContactForm() {
  const [contact, setContact] = useState(INITIAL_CONTACT);
  const [contactLoading, setContactLoading] = useState(false);
  const [contactSubmitted, setContactSubmitted] = useState(false);
  const [contactError, setContactError] = useState('');
  const [agreeContact, setAgreeContact] = useState(false);

  const [enquiry, setEnquiry] = useState(INITIAL_ENQUIRY);
  const [enquiryLoading, setEnquiryLoading] = useState(false);
  const [enquirySubmitted, setEnquirySubmitted] = useState(false);
  const [enquiryError, setEnquiryError] = useState('');
  const [agreeEnquiry, setAgreeEnquiry] = useState(false);

  const [categoriesList, setCategoriesList] = useState([]);
  
  useEffect(() => {
    supabase
      .from('categories')
      .select('categories')
      .order('id', { ascending: true })
      .then(({ data, error }) => {
        if (!error && data) setCategoriesList(data.map((c) => c.categories));
      });
  }, []);

  const countToday = async (table, email) => {
    const todayStart = new Date();
    todayStart.setHours(0, 0, 0, 0);
    const { count, error } = await supabase
      .from(table)
      .select('id', { count: 'exact', head: true })
      .eq('email', email)
      .gte('created_at', todayStart.toISOString());
    if (error) throw error;
    return count || 0;
  };

  const handleContactChange = (e) => {
    const { name, value } = e.target;
    setContact((c) => ({ ...c, [name]: value }));
  };

  const handleSubmitContact = async (e) => {
    e.preventDefault();
    setContactError('');
    setContactLoading(true);

    const trimmed = {
      name: contact.name.trim(),
      email: contact.email.trim(),
      message: contact.message.trim(),
    };

    if (!trimmed.name || !trimmed.email || !trimmed.message) {
      setContactError('All fields are required.');
      setContactLoading(false);
      return;
    }

    if (!agreeContact) {
      setContactError('You must agree to allow us to collect your email.');
      setContactLoading(false);
      return;
    }

    try {
      const count = await countToday('contacts', trimmed.email);
      if (count >= 3) {
        setContactError('You have reached 3 messages today. Try again tomorrow.');
        setContactLoading(false);
        return;
      }
      const { error } = await supabase.from('contacts').insert([trimmed]);
      if (error) throw error;
      setContactSubmitted(true);
      setContact(INITIAL_CONTACT);
    } catch (err) {
      setContactError(err.message);
    } finally {
      setContactLoading(false);
    }
  };

  const handleEnquiryChange = (e) => {
    const { name, value, type, checked } = e.target;
    if (type === 'checkbox') {
      setEnquiry((q) => ({ ...q, services: { ...q.services, [name]: checked } }));
    } else {
      setEnquiry((q) => ({ ...q, [name]: value }));
    }
  };

  const handleSubmitEnquiry = async (e) => {
    e.preventDefault();
    setEnquiryError('');
    setEnquiryLoading(true);

    if (!agreeEnquiry) {
      setEnquiryError('You must agree to allow us to collect your email and phone.');
      setEnquiryLoading(false);
      return;
    }

    try {
      const count = await countToday('event_enquiries', enquiry.email);
      if (count >= 3) {
        setEnquiryError('You have reached 3 enquiries today. Try again tomorrow.');
        setEnquiryLoading(false);
        return;
      }
      const servicesArray = Object.entries(enquiry.services).filter(([, v]) => v).map(([k]) => k);
      const payload = {
        ...enquiry,
        Budget: enquiry.Budget === '' ? null : Number(enquiry.Budget),
        services: servicesArray,
      };
      const { error } = await supabase.from('event_enquiries').insert([payload]);
      if (error) throw error;
      setEnquirySubmitted(true);
      setEnquiry(INITIAL_ENQUIRY);
    } catch (err) {
      setEnquiryError(err.message);
    } finally {
      setEnquiryLoading(false);
    }
  };

  return (
    <section className="py-20 px-6 md:px-20 bg-gradient-to-br from-slate-50 to-white">
      <div className="max-w-7xl mx-auto">
        {/* Header Section */}
        <div className="text-center mb-16">
          <div className="inline-flex items-center justify-center w-16 h-16 bg-gradient-to-br from-orange-500 to-amber-500 rounded-full mb-6 shadow-lg">
            <span className="text-2xl">✉️</span>
          </div>
          <h2 className="text-4xl md:text-5xl font-bold bg-gradient-to-r from-orange-600 to-amber-600 bg-clip-text text-transparent mb-4">
            Let's Connect
          </h2>
          <p className="text-xl text-gray-600 max-w-2xl mx-auto leading-relaxed">
            Whether it's a question or a full event plan, we're here to help bring your vision to life
          </p>
          <div className="w-24 h-1 bg-gradient-to-r from-orange-500 to-amber-500 mx-auto mt-6 rounded-full"></div>
        </div>

        <div className="grid grid-cols-1 lg:grid-cols-2 gap-12">
          {/* Contact Form */}
          <div className="group">
            <div className="bg-white rounded-2xl shadow-xl border border-gray-100 overflow-hidden transition-all duration-300 hover:shadow-2xl hover:-translate-y-1">
              <div className="bg-gradient-to-r from-orange-500 to-amber-500 p-6 text-white relative overflow-hidden">
                <div className="absolute inset-0 bg-black/10"></div>
                <div className="relative flex items-center justify-between">
                  <div>
                    <h3 className="text-2xl font-bold mb-2">Contact Us</h3>
                    <p className="text-orange-100">Get in touch with our team</p>
                  </div>
                  <ChatBubbleLeftEllipsisIcon className="w-10 h-10 text-white/80" />
                </div>
              </div>
              
              <div className="p-8">
                {contactSubmitted ? (
                  <div className="text-center py-8">
                    <CheckCircleIcon className="w-16 h-16 text-green-500 mx-auto mb-4" />
                    <h4 className="text-xl font-semibold text-gray-800 mb-2">Message Sent!</h4>
                    <p className="text-gray-600">Thanks for reaching out. We'll be in touch soon.</p>
                  </div>
                ) : (
                  <form onSubmit={handleSubmitContact} className="space-y-6">
                    {contactError && (
                      <div className="flex items-center space-x-2 p-4 bg-red-50 border border-red-200 rounded-lg">
                        <ExclamationTriangleIcon className="w-5 h-5 text-red-500 flex-shrink-0" />
                        <p className="text-red-700 text-sm">{contactError}</p>
                      </div>
                    )}
                    
                    <div className="space-y-4">
                      <div>
                        <label className="block text-sm font-medium text-gray-700 mb-2">Name *</label>
                        <input
                          name="name"
                          value={contact.name}
                          onChange={handleContactChange}
                          placeholder="Enter your full name"
                          className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-orange-500 focus:border-transparent transition-all duration-200 bg-gray-50 focus:bg-white"
                          required
                        />
                      </div>
                      
                      <div>
                        <label className="block text-sm font-medium text-gray-700 mb-2">Email *</label>
                        <input
                          name="email"
                          value={contact.email}
                          onChange={handleContactChange}
                          placeholder="your.email@example.com"
                          type="email"
                          className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-orange-500 focus:border-transparent transition-all duration-200 bg-gray-50 focus:bg-white"
                          required
                        />
                      </div>
                      
                      <div>
                        <label className="block text-sm font-medium text-gray-700 mb-2">Message *</label>
                        <textarea
                          name="message"
                          value={contact.message}
                          onChange={handleContactChange}
                          placeholder="Tell us about your inquiry..."
                          className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-orange-500 focus:border-transparent transition-all duration-200 bg-gray-50 focus:bg-white resize-none"
                          rows={4}
                          required
                        />
                      </div>
                    </div>

                    <div className="bg-gray-50 p-4 rounded-lg">
                      <label className="flex items-start space-x-3 text-sm cursor-pointer">
                        <input
                          type="checkbox"
                          checked={agreeContact}
                          onChange={(e) => setAgreeContact(e.target.checked)}
                          className="mt-1 w-4 h-4 text-orange-500 border-gray-300 rounded focus:ring-orange-500"
                        />
                        <span className="text-gray-600 leading-relaxed">
                          By clicking Agree, you consent to us storing your email address for follow-up and moderation purposes.
                        </span>
                      </label>
                    </div>

                    <button
                      type="submit"
                      disabled={contactLoading}
                      className="w-full bg-gradient-to-r from-orange-500 to-amber-500 text-white px-8 py-4 rounded-lg font-semibold text-lg shadow-lg hover:shadow-xl transform hover:-translate-y-0.5 transition-all duration-200 disabled:opacity-50 disabled:cursor-not-allowed disabled:transform-none"
                    >
                      {contactLoading ? (
                        <div className="flex items-center justify-center space-x-2">
                          <div className="w-5 h-5 border-2 border-white border-t-transparent rounded-full animate-spin"></div>
                          <span>Sending...</span>
                        </div>
                      ) : (
                        'Send Message'
                      )}
                    </button>
                  </form>
                )}
              </div>
            </div>
          </div>

          {/* Event Enquiry Form */}
          <div className="group">
            <div className="bg-white rounded-2xl shadow-xl border border-gray-100 overflow-hidden transition-all duration-300 hover:shadow-2xl hover:-translate-y-1">
              <div className="bg-gradient-to-r from-amber-500 to-orange-500 p-6 text-white relative overflow-hidden">
                <div className="absolute inset-0 bg-black/10"></div>
                <div className="relative flex items-center justify-between">
                  <div>
                    <h3 className="text-2xl font-bold mb-2">Event Enquiry</h3>
                    <p className="text-amber-100">Plan your perfect event</p>
                  </div>
                  <CalendarDaysIcon className="w-10 h-10 text-white/80" />
                </div>
              </div>
              
              <div className="p-8">
                {enquirySubmitted ? (
                  <div className="text-center py-8">
                    <CheckCircleIcon className="w-16 h-16 text-green-500 mx-auto mb-4" />
                    <h4 className="text-xl font-semibold text-gray-800 mb-2">Enquiry Submitted!</h4>
                    <p className="text-gray-600">Thanks for your interest. We'll respond shortly with a detailed proposal.</p>
                  </div>
                ) : (
                  <form onSubmit={handleSubmitEnquiry} className="space-y-6">
                    {enquiryError && (
                      <div className="flex items-center space-x-2 p-4 bg-red-50 border border-red-200 rounded-lg">
                        <ExclamationTriangleIcon className="w-5 h-5 text-red-500 flex-shrink-0" />
                        <p className="text-red-700 text-sm">{enquiryError}</p>
                      </div>
                    )}
                    
                    <div className="grid grid-cols-1 gap-4">
                      <div>
                        <label className="block text-sm font-medium text-gray-700 mb-2">Full Name *</label>
                        <input
                          name="full_name"
                          value={enquiry.full_name}
                          onChange={handleEnquiryChange}
                          placeholder="Enter your full name"
                          className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-amber-500 focus:border-transparent transition-all duration-200 bg-gray-50 focus:bg-white"
                          required
                        />
                      </div>
                      
                      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                        <div>
                          <label className="block text-sm font-medium text-gray-700 mb-2">Email *</label>
                          <input
                            name="email"
                            value={enquiry.email}
                            onChange={handleEnquiryChange}
                            placeholder="your.email@example.com"
                            type="email"
                            className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-amber-500 focus:border-transparent transition-all duration-200 bg-gray-50 focus:bg-white"
                            required
                          />
                        </div>
                        
                        <div>
                          <label className="block text-sm font-medium text-gray-700 mb-2">Phone Number *</label>
                          <input
                            name="phone"
                            value={enquiry.phone}
                            onChange={handleEnquiryChange}
                            placeholder="+1 (555) 123-4567"
                            type="tel"
                            className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-amber-500 focus:border-transparent transition-all duration-200 bg-gray-50 focus:bg-white"
                            required
                          />
                        </div>
                      </div>
                      
                      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                        <div>
                          <label className="block text-sm font-medium text-gray-700 mb-2">Event Date *</label>
                          <input
                            name="event_date"
                            value={enquiry.event_date}
                            onChange={handleEnquiryChange}
                            type="date"
                            className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-amber-500 focus:border-transparent transition-all duration-200 bg-gray-50 focus:bg-white"
                            required
                          />
                        </div>
                        
                        <div>
                          <label className="block text-sm font-medium text-gray-700 mb-2">Event Location *</label>
                          <input
                            name="location"
                            value={enquiry.location}
                            onChange={handleEnquiryChange}
                            placeholder="City, State or Venue"
                            className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-amber-500 focus:border-transparent transition-all duration-200 bg-gray-50 focus:bg-white"
                            required
                          />
                        </div>
                      </div>
                    </div>

                    <div className="bg-gray-50 p-6 rounded-lg">
                      <h4 className="text-sm font-medium text-gray-700 mb-4">Services Interested In:</h4>
                      <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                        {categoriesList.map((cat) => (
                          <label key={cat} className="flex items-center space-x-3 cursor-pointer group">
                            <input
                              type="checkbox"
                              name={cat}
                              checked={!!enquiry.services[cat]}
                              onChange={handleEnquiryChange}
                              className="w-4 h-4 text-amber-500 border-gray-300 rounded focus:ring-amber-500"
                            />
                            <span className="text-gray-700 group-hover:text-gray-900 transition-colors duration-200">{cat}</span>
                          </label>
                        ))}
                      </div>
                    </div>

                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-2">Estimated Budget (Optional)</label>
                      <input
                        name="Budget"
                        value={enquiry.Budget}
                        onChange={handleEnquiryChange}
                        placeholder="e.g., $5,000"
                        className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-amber-500 focus:border-transparent transition-all duration-200 bg-gray-50 focus:bg-white"
                      />
                    </div>

                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-2">Event Details *</label>
                      <textarea
                        name="details"
                        value={enquiry.details}
                        onChange={handleEnquiryChange}
                        placeholder="Tell us about your event vision, guest count, special requirements..."
                        rows={4}
                        className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-amber-500 focus:border-transparent transition-all duration-200 bg-gray-50 focus:bg-white resize-none"
                        required
                      />
                    </div>

                    <div className="bg-gray-50 p-4 rounded-lg">
                      <label className="flex items-start space-x-3 text-sm cursor-pointer">
                        <input
                          type="checkbox"
                          checked={agreeEnquiry}
                          onChange={(e) => setAgreeEnquiry(e.target.checked)}
                          className="mt-1 w-4 h-4 text-amber-500 border-gray-300 rounded focus:ring-amber-500"
                        />
                        <span className="text-gray-600 leading-relaxed">
                          By clicking Agree, you consent to us storing your email and phone number for planning and coordination purposes.
                        </span>
                      </label>
                    </div>

                    <button
                      type="submit"
                      disabled={enquiryLoading}
                      className="w-full bg-gradient-to-r from-amber-500 to-orange-500 text-white px-8 py-4 rounded-lg font-semibold text-lg shadow-lg hover:shadow-xl transform hover:-translate-y-0.5 transition-all duration-200 disabled:opacity-50 disabled:cursor-not-allowed disabled:transform-none"
                    >
                      {enquiryLoading ? (
                        <div className="flex items-center justify-center space-x-2">
                          <div className="w-5 h-5 border-2 border-white border-t-transparent rounded-full animate-spin"></div>
                          <span>Submitting...</span>
                        </div>
                      ) : (
                        'Submit Enquiry'
                      )}
                    </button>
                  </form>
                )}
              </div>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
}